import dedent from 'dedent';
import { ArtifactModes, shadcnComponents } from 'librechat-data-provider';
import { langSubset } from './languages';
import type {
  SandpackProviderProps,
  SandpackPredefinedTemplate,
} from '@codesandbox/sandpack-react';

function detectLanguageFromContent(content: string): string | null {
  const patterns = {
    python:
      /^(import\s+|from\s+\w+\s+import\s+|def\s+\w+\s*\(|class\s+\w+:|if\s+__name__\s*==\s*['"]__main__['"]:)/m,
    javascript:
      /^(import\s+|export\s+|const\s+|let\s+|class\s+\w+\s*{|function\s+\w+\s*\(|module\.exports\s*=)/m,
    typescript:
      /^(import\s+|export\s+|interface\s+|type\s+|class\s+\w+\s*{|const\s+\w+:\s*)/m,
    html: /^<!DOCTYPE\s+html|<html|<body|<head|<script|<style/i,
    css: /^(\s*[\w-]+\s*:\s*[\w-]+|@media\s+|@import\s+|@keyframes\s+)/m,
    java: /^(package\s+|import\s+|public\s+class\s+|class\s+\w+\s*{)/m,
    rust: /^(use\s+|fn\s+|pub\s+|struct\s+|impl\s+)/m,
    go: /^(package\s+|import\s+|func\s+|type\s+)/m,
    ruby: /^(require\s+|class\s+|def\s+|module\s+)/m,
    php: /^(<\?php|namespace\s+|use\s+|class\s+|function\s+)/m,
    shell: /^(#!\s*\/.*\/(?:bash|sh|zsh)|export\s+|source\s+|\$\{|\$\()/m,
  };

  for (const [lang, pattern] of Object.entries(patterns)) {
    if (pattern.test(content)) {
      return lang;
    }
  }

  return null;
}

export const getArtifactsMode = ({
  codeArtifacts,
  includeShadcnui,
  customPromptMode,
}: {
  codeArtifacts: boolean;
  includeShadcnui: boolean;
  customPromptMode: boolean;
}): ArtifactModes | undefined => {
  if (codeArtifacts === false) {
    return undefined;
  } else if (customPromptMode === true) {
    return ArtifactModes.CUSTOM;
  } else if (includeShadcnui === true) {
    return ArtifactModes.SHADCNUI;
  }
  return ArtifactModes.DEFAULT;
};

const artifactFilename = {
  'application/vnd.mermaid': 'App.tsx',
  'application/vnd.react': 'App.tsx',
  'text/html': 'index.html',
  'application/vnd.code-html': 'index.html',
  default: 'index.html',
};

const artifactTemplate: Record<
  keyof typeof artifactFilename,
  SandpackPredefinedTemplate | undefined
> = {
  'text/html': 'static',
  'application/vnd.react': 'react-ts',
  'application/vnd.mermaid': 'react-ts',
  'application/vnd.code-html': 'static',
  default: 'static',
};

export function getFileExtension(language?: string, content?: string): string {
  switch (language) {
    case 'application/vnd.react':
      return 'tsx';
    case 'application/vnd.mermaid':
      return 'mermaid';
    case 'text/html':
    case 'application/vnd.code-html':
      return 'html';
    case 'text/x-python':
    case 'application/x-python':
    case 'text/python':
    case 'application/python':
      return 'python';
    case 'text/plain':
      // For plain text, try to detect the language from content
      if (content !== undefined && content !== '') {
        const detectedLang = detectLanguageFromContent(content);
        if (detectedLang !== null) {
          return detectedLang;
        }
      }
      return 'txt';
  }

  if (
    language !== undefined &&
    language !== '' &&
    language.startsWith('text/')
  ) {
    const subtype = language.split('/')[1];
    switch (subtype) {
      case 'javascript':
        return 'javascript';
      case 'typescript':
        return 'typescript';
      case 'css':
        return 'css';
      case 'x-python':
      case 'python':
        return 'python';
      case 'x-java':
        return 'java';
      case 'x-c':
        return 'c';
      case 'x-cpp':
        return 'cpp';
      case 'x-csharp':
        return 'csharp';
      case 'x-ruby':
        return 'ruby';
      case 'x-php':
        return 'php';
      case 'x-go':
        return 'go';
      case 'x-rust':
        return 'rust';
      case 'markdown':
        return 'markdown';
      case 'x-sql':
        return 'sql';
      case 'x-shell':
      case 'x-sh':
        return 'shell';
      case 'yaml':
        return 'yaml';
      case 'xml':
        return 'xml';
      default:
        if (langSubset.includes(subtype)) {
          return subtype;
        }
        return 'txt';
    }
  }

  if (
    language !== undefined &&
    language !== '' &&
    language.startsWith('application/')
  ) {
    const subtype = language.split('/')[1];
    switch (subtype) {
      case 'javascript':
      case 'x-javascript':
        return 'javascript';
      case 'typescript':
        return 'typescript';
      case 'json':
        return 'json';
      case 'xml':
        return 'xml';
      case 'x-python':
      case 'python':
        return 'python';
      case 'x-java':
        return 'java';
      case 'x-ruby':
        return 'ruby';
      case 'x-php':
        return 'php';
      case 'x-yaml':
      case 'yaml':
        return 'yaml';
      case 'x-shellscript':
        return 'shell';
      default:
        return 'txt';
    }
  }

  if (language !== undefined && language !== '' && language.includes('.')) {
    const ext = language.split('.').pop();
    if (ext === undefined || ext === '') {
      return 'txt';
    }

    switch (ext.toLowerCase()) {
      case 'js':
        return 'javascript';
      case 'ts':
        return 'typescript';
      case 'jsx':
        return 'javascript';
      case 'tsx':
        return 'typescript';
      case 'py':
        return 'python';
      case 'java':
        return 'java';
      case 'c':
        return 'c';
      case 'cpp':
      case 'cc':
      case 'cxx':
        return 'cpp';
      case 'cs':
        return 'csharp';
      case 'rb':
        return 'ruby';
      case 'php':
        return 'php';
      case 'go':
        return 'go';
      case 'rs':
        return 'rust';
      case 'md':
        return 'markdown';
      case 'sql':
        return 'sql';
      case 'sh':
      case 'bash':
        return 'shell';
      case 'yml':
      case 'yaml':
        return 'yaml';
      case 'xml':
        return 'xml';
      case 'css':
        return 'css';
      case 'html':
        return 'html';
      case 'json':
        return 'json';
      default:
        if (langSubset.includes(ext)) {
          return ext;
        }
        return 'txt';
    }
  }

  if (
    language !== undefined &&
    language !== '' &&
    langSubset.includes(language)
  ) {
    return language;
  }

  if (content !== undefined && content !== '') {
    const detectedLang = detectLanguageFromContent(content);
    if (detectedLang !== null) {
      return detectedLang;
    }
  }

  return 'txt';
}

export function getKey(type: string, language?: string): string {
  return `${type}${
    language !== undefined && language.length > 0 ? `-${language}` : ''
  }`;
}

export function getArtifactFilename(type: string, language?: string): string {
  const key = getKey(type, language);
  return artifactFilename[key] ?? artifactFilename.default;
}

export function getTemplate(
  type: string,
  language?: string,
): SandpackPredefinedTemplate {
  const key = getKey(type, language);
  return (
    artifactTemplate[key] ??
    (artifactTemplate.default as SandpackPredefinedTemplate)
  );
}

const standardDependencies = {
  three: '^0.167.1',
  'lucide-react': '^0.394.0',
  'react-router-dom': '^6.11.2',
  'class-variance-authority': '^0.6.0',
  clsx: '^1.2.1',
  'date-fns': '^3.3.1',
  'tailwind-merge': '^1.9.1',
  'tailwindcss-animate': '^1.0.5',
  recharts: '2.12.7',
  '@radix-ui/react-accordion': '^1.1.2',
  '@radix-ui/react-alert-dialog': '^1.0.2',
  '@radix-ui/react-aspect-ratio': '^1.1.0',
  '@radix-ui/react-avatar': '^1.1.0',
  '@radix-ui/react-checkbox': '^1.0.3',
  '@radix-ui/react-collapsible': '^1.0.3',
  '@radix-ui/react-dialog': '^1.0.2',
  '@radix-ui/react-dropdown-menu': '^2.1.1',
  '@radix-ui/react-hover-card': '^1.0.5',
  '@radix-ui/react-label': '^2.0.0',
  '@radix-ui/react-menubar': '^1.1.1',
  '@radix-ui/react-navigation-menu': '^1.2.0',
  '@radix-ui/react-popover': '^1.0.7',
  '@radix-ui/react-progress': '^1.1.0',
  '@radix-ui/react-radio-group': '^1.1.3',
  '@radix-ui/react-select': '^2.0.0',
  '@radix-ui/react-separator': '^1.0.3',
  '@radix-ui/react-slider': '^1.1.1',
  '@radix-ui/react-switch': '^1.0.3',
  '@radix-ui/react-tabs': '^1.0.3',
  '@radix-ui/react-toast': '^1.1.5',
  '@radix-ui/react-tooltip': '^1.0.6',
  '@radix-ui/react-slot': '^1.1.0',
  '@radix-ui/react-toggle': '^1.1.0',
  '@radix-ui/react-toggle-group': '^1.1.0',
  'embla-carousel-react': '^8.2.0',
  'react-day-picker': '^9.0.8',
  'dat.gui': '^0.7.9',
  vaul: '^0.9.1',
};

const mermaidDependencies = Object.assign(
  {
    mermaid: '^11.4.1',
    'react-zoom-pan-pinch': '^3.6.1',
  },
  standardDependencies,
);

const dependenciesMap: Record<keyof typeof artifactFilename, object> = {
  'application/vnd.mermaid': mermaidDependencies,
  'application/vnd.react': standardDependencies,
  'text/html': standardDependencies,
  'application/vnd.code-html': standardDependencies,
  default: standardDependencies,
};

export function getDependencies(type: string): Record<string, string> {
  return dependenciesMap[type] ?? standardDependencies;
}

export function getProps(type: string): Partial<SandpackProviderProps> {
  return {
    customSetup: {
      dependencies: getDependencies(type),
    },
  };
}

export const sharedOptions: SandpackProviderProps['options'] = {
  externalResources: [
    'https://unpkg.com/@tailwindcss/ui/dist/tailwind-ui.min.css',
  ],
};

export const sharedFiles = {
  '/lib/utils.ts': shadcnComponents.utils,
  '/components/ui/accordion.tsx': shadcnComponents.accordian,
  '/components/ui/alert-dialog.tsx': shadcnComponents.alertDialog,
  '/components/ui/alert.tsx': shadcnComponents.alert,
  '/components/ui/avatar.tsx': shadcnComponents.avatar,
  '/components/ui/badge.tsx': shadcnComponents.badge,
  '/components/ui/breadcrumb.tsx': shadcnComponents.breadcrumb,
  '/components/ui/button.tsx': shadcnComponents.button,
  '/components/ui/calendar.tsx': shadcnComponents.calendar,
  '/components/ui/card.tsx': shadcnComponents.card,
  '/components/ui/carousel.tsx': shadcnComponents.carousel,
  '/components/ui/checkbox.tsx': shadcnComponents.checkbox,
  '/components/ui/collapsible.tsx': shadcnComponents.collapsible,
  '/components/ui/dialog.tsx': shadcnComponents.dialog,
  '/components/ui/drawer.tsx': shadcnComponents.drawer,
  '/components/ui/dropdown-menu.tsx': shadcnComponents.dropdownMenu,
  '/components/ui/input.tsx': shadcnComponents.input,
  '/components/ui/label.tsx': shadcnComponents.label,
  '/components/ui/menubar.tsx': shadcnComponents.menuBar,
  '/components/ui/navigation-menu.tsx': shadcnComponents.navigationMenu,
  '/components/ui/pagination.tsx': shadcnComponents.pagination,
  '/components/ui/popover.tsx': shadcnComponents.popover,
  '/components/ui/progress.tsx': shadcnComponents.progress,
  '/components/ui/radio-group.tsx': shadcnComponents.radioGroup,
  '/components/ui/select.tsx': shadcnComponents.select,
  '/components/ui/separator.tsx': shadcnComponents.separator,
  '/components/ui/skeleton.tsx': shadcnComponents.skeleton,
  '/components/ui/slider.tsx': shadcnComponents.slider,
  '/components/ui/switch.tsx': shadcnComponents.switchComponent,
  '/components/ui/table.tsx': shadcnComponents.table,
  '/components/ui/tabs.tsx': shadcnComponents.tabs,
  '/components/ui/textarea.tsx': shadcnComponents.textarea,
  '/components/ui/toast.tsx': shadcnComponents.toast,
  '/components/ui/toaster.tsx': shadcnComponents.toaster,
  '/components/ui/toggle-group.tsx': shadcnComponents.toggleGroup,
  '/components/ui/toggle.tsx': shadcnComponents.toggle,
  '/components/ui/tooltip.tsx': shadcnComponents.tooltip,
  '/components/ui/use-toast.tsx': shadcnComponents.useToast,
  '/public/index.html': dedent`
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Document</title>
        <script src="https://cdn.tailwindcss.com"></script>
      </head>
      <body>
        <div id="root"></div>
      </body>
    </html>
  `,
};

export function preprocessCodeArtifacts(
  text?: string,
): string | null | undefined {
  if (typeof text !== 'string') {
    return text as null | undefined;
  }

  if (text === '') {
    return '';
  }

  // Remove <thinking> tags and their content
  text = text.replace(/<thinking>[\s\S]*?<\/thinking>|<thinking>[\s\S]*/g, '');

  // Process artifact headers
  const regex = /(^|\n)(:::artifact[\s\S]*?(?:```[\s\S]*?```|$))/g;
  return text.replace(regex, (match, newline, artifactBlock) => {
    if (artifactBlock.includes('```') === true) {
      // Keep artifact headers with code blocks (empty or not)
      return newline + artifactBlock;
    }
    // Remove artifact headers without code blocks, but keep the newline
    return newline;
  });
}
